/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import { Form } from 'react-bootstrap'
import { toast } from '../../../services/toast'
import { Modal } from '../../Modal'
import { FsiPropertiesTable } from '../../doc-preview/properties/FsiPropertiesTable'
import LoadingSpinner from '../../LoadingSpinner'
import { FsiPropertiesConfirmModal } from './FsiPropertiesConfirmModal'
import type { MetadataElement, FilePropertiesDetail } from '../../../services/api/files'
import type { SelectOption } from '../../../utils/selectOptions'
import * as metadataApi from '../../../services/api/api.metadata'
import { usersApiService } from '../../../services/api/users'
import { styled } from 'styled-components'

const StyledFormGroup = styled(Form.Group)`
  margin-bottom: 15px;
  &.w100p {
    width: 100%;
  }
  &.w80p {
    width: 80%;
  }
`

const GroupType = {
  CLOUDADMIN_ACL: 'CLOUDADMIN_ACL'
} as const

interface FsiPropertiesModalProps {
  fsiId: number | string
  onClose: () => void
  onSuccess?: () => void
  isMDF?: boolean
  MDFType?: string
}

/**
 * File/Folder Properties Modal
 *
 * Displays and allows editing of file/folder properties including:
 * - Metadata
 * - ACLs (Access Control Lists)
 * - File attributes
 * - Permissions
 *
 * Wraps FsiProperties component in a modal dialog
 */
export const FsiPropertiesModal: FC<FsiPropertiesModalProps> = ({ fsiId, onClose, onSuccess, isMDF, MDFType }) => {
  const { t } = useTranslation()
  const { api } = useServiceContext()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [fileDetails, setFileDetails] = useState<FilePropertiesDetail | null>(null)
  const [assembledMetadata, setAssembledMetadata] = useState<MetadataElement[]>([])
  const [assembledMetadataCopy, setAssembledMetadataCopy] = useState<MetadataElement[]>([])
  const [userOptions, setUserOptions] = useState<SelectOption[] | null>(null)
  const [groupOptions, setGroupOptions] = useState<SelectOption[] | null>(null)
  const [groupOptionsCaRolesDisabled, setGroupOptionsCaRolesDisabled] = useState<SelectOption[] | null>(null)
  const [userOptionsMap, setUserOptionsMap] = useState<Map<number, any>>(new Map())
  const [groupOptionsMap, setGroupOptionsMap] = useState<Map<number, any>>(new Map())
  const propertiesAclsIds = useRef<number[] | null>(null)

  useEffect(() => {
    loadFileDetails()
  }, [fsiId])

  // Track if there are any changes in the metadata
  useEffect(() => {
    const changed = assembledMetadata.some((prop) => prop.isChanged || prop.revertToInherited)
    setHasChanges(changed)
  }, [assembledMetadata])

  const loadFileDetails = async () => {
    setIsLoading(true)
    try {
      // First get file properties to extract metadata template IDs
      const properties = await api.files.getDetailsForProperties(Number(fsiId))

      // Extract metadata template IDs from properties
      const filePropIds = properties.metadata?.elements?.map((item) => item.metadataTemplateId) || []

      // Make all API calls in parallel
      const [detail, metadataTemplates, assembledUsersGroups] = await Promise.all([
        api.files.getDetails(Number(fsiId)), // /be/api/v2/files/{id}/detail
        metadataApi.getAll({ id: filePropIds.length ? filePropIds : 0 }), // /be/api/metadatatemplates?id=...
        usersApiService.getAssembledUsersGroups() // Load users and groups
      ])

      // Transform users to SelectOption format and create Map for lookup
      const userOpts: SelectOption[] = assembledUsersGroups.users.map((user) => ({
        ...user,
        value: user.id,
        label: user.chName || user.name || ''
      }))

      const userMap = new Map<number, any>()
      assembledUsersGroups.users.forEach((user) => {
        userMap.set(user.id, user)
      })

      // Transform groups to SelectOption format and create Map for lookup
      const groupOpts: SelectOption[] = assembledUsersGroups.groups.map((group) => ({
        ...group,
        value: group.id,
        label: group.name || ''
      }))

      const groupMap = new Map<number, any>()
      assembledUsersGroups.groups.forEach((group) => {
        groupMap.set(group.id, group)
      })

      // Apply CA group disabled logic matching legacy FsiPropertiesModal.jsx
      const groupOptsDisabled: SelectOption[] = groupOpts.map((option: any) => {
        const isCAGroupDisabled = option.type === GroupType.CLOUDADMIN_ACL
        return {
          ...option,
          label: option.label,
          value: option.value,
          isDisabled: isCAGroupDisabled,
          isFixed: isCAGroupDisabled
        }
      })

      setUserOptions(userOpts)
      setGroupOptions(groupOpts)
      setGroupOptionsCaRolesDisabled(groupOptsDisabled)
      setUserOptionsMap(userMap)
      setGroupOptionsMap(groupMap)

      // Merge template data into metadata elements
      const assembledWithTemplates: MetadataElement[] = (properties.metadata?.elements || []).map((element) => {
        const template = metadataTemplates.elements.find((t) => t.id === element.metadataTemplateId)
        return {
          ...element,
          template: template
            ? {
                id: template.id,
                name: template.name,
                type: template.type,
                definition: template.definition,
                editableInCR: (template as any).editableInCR
              }
            : undefined
        }
      })

      setFileDetails(properties)
      setAssembledMetadata(assembledWithTemplates)

      // Create deep copy for revert functionality
      setAssembledMetadataCopy(JSON.parse(JSON.stringify(assembledWithTemplates)))
    } catch (error) {
      console.error('Failed to load file details:', error)
      toast.error(t('FAILED_TO_LOAD_FILE_DETAILS'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeItem = (property: MetadataElement, value: any) => {
    // Update metadata and reset revertToInherited flag since user is making a new change
    // Also mark as changed immediately when value is modified
    const updated = assembledMetadata.map((item) =>
      item.metadataTemplateId === property.metadataTemplateId
        ? { ...item, value, revertToInherited: false, isChanged: true }
        : item
    )
    setAssembledMetadata(updated)

    // Also update the copy to reflect the change
    const updatedCopy = assembledMetadataCopy.map((item) =>
      item.metadataTemplateId === property.metadataTemplateId ? { ...item, value } : item
    )
    setAssembledMetadataCopy(updatedCopy)
  }

  const handleAcceptItem = (property: MetadataElement) => {
    // Mark as changed and update the copy to match current value
    const updated = assembledMetadata.map((item) =>
      item.metadataTemplateId === property.metadataTemplateId ? { ...item, isChanged: true } : item
    )
    setAssembledMetadata(updated)

    // Update the copy to reflect the accepted change
    const updatedCopy = assembledMetadataCopy.map((item) =>
      item.metadataTemplateId === property.metadataTemplateId ? { ...item, value: property.value } : item
    )
    setAssembledMetadataCopy(updatedCopy)
  }

  const handleCancelItem = (property: MetadataElement) => {
    // Revert to the last accepted value (from copy)
    const originalProperty = assembledMetadataCopy.find(
      (item) => item.metadataTemplateId === property.metadataTemplateId
    )

    if (originalProperty) {
      const updated = assembledMetadata.map((item) =>
        item.metadataTemplateId === property.metadataTemplateId
          ? { ...item, value: originalProperty.value, isChanged: false, revertToInherited: false }
          : item
      )
      setAssembledMetadata(updated)
    }
  }

  const handleRevertItem = (property: MetadataElement) => {
    // Revert to inherited value or mark for reversion
    const updated = assembledMetadata.map((item) =>
      item.metadataTemplateId === property.metadataTemplateId
        ? {
            ...item,
            revertToInherited: true,
            value: property.inherited ? property.inheritedValue : null
          }
        : item
    )
    setAssembledMetadata(updated)

    // Also update the copy
    const updatedCopy = assembledMetadataCopy.map((item) =>
      item.metadataTemplateId === property.metadataTemplateId
        ? {
            ...item,
            revertToInherited: true,
            value: property.inherited ? property.inheritedValue : null
          }
        : item
    )
    setAssembledMetadataCopy(updatedCopy)
  }

  const mapValueToObject = (property: MetadataElement, value: any) => {
    const type = property.template?.type

    if (!type) return value

    // Handle user select types (SELECT_ONE_USER, SELECT_MULTI_USER)
    if (type.includes('USER')) {
      if (Array.isArray(value)) {
        // Multi-select: map array of IDs to user objects
        return value
          .map((id) => {
            const user = userOptionsMap.get(typeof id === 'number' ? id : id?.id)
            return user || id
          })
          .filter(Boolean)
      } else {
        // Single select: map ID to user object
        const userId = typeof value === 'number' ? value : value?.id
        return userId ? userOptionsMap.get(userId) || value : value
      }
    }

    // Handle group select types (SELECT_ONE_GROUP, SELECT_MULTI_GROUP)
    if (type.includes('GROUP')) {
      if (Array.isArray(value)) {
        // Multi-select: map array of IDs to group objects
        return value
          .map((id) => {
            const group = groupOptionsMap.get(typeof id === 'number' ? id : id?.id)
            return group || id
          })
          .filter(Boolean)
      } else {
        // Single select: map ID to group object
        const groupId = typeof value === 'number' ? value : value?.id
        return groupId ? groupOptionsMap.get(groupId) || value : value
      }
    }

    // For all other types, return value as-is
    return value
  }

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmModal(true)
    } else {
      onClose()
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Filter properties that have been changed or marked for revert
      const changedProperties = assembledMetadata.filter((prop) => prop.isChanged || prop.revertToInherited)

      if (changedProperties.length === 0) {
        toast.info(t('NO_CHANGES_TO_SAVE'))
        return
      }

      // Build payload array - send full property object like legacy (clone and map values)
      const payload: MetadataElement[] = changedProperties.map((prop) => {
        const clonedProp = { ...prop }
        clonedProp.value = mapValueToObject(prop, prop.value)

        if (prop.template?.type === 'DATETIME' && clonedProp.value) {
          if ((clonedProp.value as any).utc) {
            // moment.js object
            const utcDate = (clonedProp.value as any).utc(true)
            utcDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            clonedProp.value = utcDate.valueOf()
          } else if (typeof clonedProp.value === 'string') {
            clonedProp.value = new Date(clonedProp.value).getTime()
          }
        }

        return clonedProp
      })

      // Call API to save metadata changes
      await api.files.modifyFileMetadatas(Number(fsiId), payload)

      // Call success callback if provided
      if (onSuccess) {
        onSuccess()
      }

      // Close the modal
      onClose()
    } catch (error: any) {
      const errorMessage = error?.message || t('FAILED_TO_SAVE_PROPERTIES')
      toast.error(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelWithConfirmation = () => {
    if (hasChanges) {
      // Show confirmation modal if there are unsaved changes
      setShowConfirmModal(true)
    } else {
      // No changes, just reload
      loadFileDetails()
    }
  }

  const handleConfirmLeave = () => {
    setShowConfirmModal(false)
    onClose()
  }

  const handleStay = () => {
    setShowConfirmModal(false)
  }

  return (
    <Modal show handleClose={handleClose} title={t('PROPERTIES')} size="md" className="fsi-properties-modal">
      <Modal.Body>
        {isLoading ? (
          <LoadingSpinner centered size={40} />
        ) : fileDetails ? (
          <form className={'clearfix'}>
            <StyledFormGroup>
              <Form.Label>{t('DOCUMENT')}</Form.Label>
              <p className="whitespaceprewrap">{fileDetails?.name}</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <Form.Label>{t('DOCUMENT_TYPE')}</Form.Label>
              <p className="whitespaceprewrap">{fileDetails.templateName || '-'}</p>
            </StyledFormGroup>
            <StyledFormGroup>
              <Form.Label>{t('PROPERTIES')}</Form.Label>
              <FsiPropertiesTable
                showJustAcls={false}
                propertiesDetails={fileDetails}
                assembledMetadata={assembledMetadata}
                assembledMetadataCopy={assembledMetadataCopy}
                propertiesInfo={{
                  isDocument: true,
                  isModal: true
                }}
                propertiesAclsIds={propertiesAclsIds}
                userOptions={userOptions}
                groupOptions={groupOptions}
                groupOptionsCaRolesDisabled={groupOptionsCaRolesDisabled}
                mapValueToObject={mapValueToObject}
                errors={{ requiredPropsAreMissing: false }}
                isEditable={true}
                onChangeItem={handleChangeItem}
                onCancelItem={handleCancelItem}
                onAcceptItem={handleAcceptItem}
                onRevertItem={handleRevertItem}
              />
            </StyledFormGroup>
          </form>
        ) : (
          <div className="text-center text-muted py-5">{t('FAILED_TO_LOAD_FILE_DETAILS')}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {hasChanges ? (
          <>
            <button className="btn btn-link" onClick={handleCancelWithConfirmation} disabled={isSaving || isLoading}>
              {t('CANCEL')}
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || isLoading}>
              {isSaving ? (
                <>
                  <LoadingSpinner size={16} /> {t('SAVING')}...
                </>
              ) : (
                t('SAVE')
              )}
            </button>
          </>
        ) : (
          <button className="btn btn-primary" onClick={handleClose} disabled={isLoading}>
            {t('OK')}
          </button>
        )}
      </Modal.Footer>

      {showConfirmModal && <FsiPropertiesConfirmModal onConfirm={handleConfirmLeave} onCancel={handleStay} />}
    </Modal>
  )
}

export default FsiPropertiesModal
